// @ts-nocheck
import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Stack,
  Typography,
  Paper,
} from '@mui/material';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';
import {
  ReactMuiTableColumnHeaderTextEllipsis,
  ReactMuiTableVirtualizedV8,
} from '@nuva-org/solytics-frontend';
import mime from 'mime';
import { useDropzone } from 'react-dropzone';

import { ReactComponent as FileTextIcon } from '../../../../assets/Icons/lucideIcons/file-text.svg';
import { ReactComponent as CloudUploadIcon } from '../../../../assets/Icons/lucideIcons/cloud-upload.svg';
import { ReactComponent as TrashIcon } from '../../../../assets/Icons/lucideIcons/trash-2.svg';
import {
  convertCsvToJson,
  createDynamicColumns,
} from '../../../../utils/Utils';
import SearchTextField from '../../SearchTextField';
import swalToast from '../../swalToast';

/**
 * Table field component for uploading and displaying tabular data (CSV)
 * @param {Object} props - Component props
 * @param {{ columns: string[], data: Object[], file_name?: string } | null} props.value - Current table value
 * @param {Function} props.onChange - Function to handle value changes
 * @returns {React.ReactElement} The Table Field component
 */
const TableFieldComponent = ({ value, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const tableData = value?.data || [];

  const handleCsvFile = useCallback(
    (file) => {
      const fType = mime.getType(file?.name);
      const csvFileType = 'text/csv';
      const isFileTypeCSV = [csvFileType].includes(file?.type)
        ? true
        : fType === csvFileType;

      if (file && isFileTypeCSV) {
        convertCsvToJson(file)
          .then((jsonArray) => {
            const parsedTableData = {
              columns: Object.keys(
                isArray(jsonArray) && jsonArray.length > 0 ? jsonArray[0] : []
              ),
              data: jsonArray,
              file_name: file.name,
            };
            setSearchTerm('');
            onChange(parsedTableData);
          })
          .catch((error) => {
            if (error) {
              swalToast({
                icon: 'error',
                title: `CSV parsing error: ${error?.message}`,
              });
            }
          });
      } else {
        swalToast({
          icon: 'error',
          title: 'File format not supported, please select CSV.',
        });
      }
    },
    [onChange, setSearchTerm]
  );

  const handleAcceptedFiles = ([file]) => {
    handleCsvFile(file);
  };

  const handleRemoveFile = useCallback(() => {
    setSearchTerm('');
    onChange(null);
  }, [onChange, setSearchTerm]);

  const handleSearchChange = useCallback(
    (event) => {
      setSearchTerm(event?.target?.value || '');
    },
    [setSearchTerm]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
    onDropAccepted: handleAcceptedFiles,
    onDropRejected: () => {
      swalToast({
        icon: 'error',
        title: 'Please select a CSV file.',
      });
    },
  });

  /**
   * Creates props for table columns
   * @function
   * @returns {Object} Column props
   */
  const handleGetTableColumnProps = useCallback(
    () => ({
      style: {
        textAlign: 'left',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        wordBreak: 'initial',
        whiteSpace: 'nowrap',
      },
    }),
    []
  );

  /**
   * Creates props for table headers
   * @function
   * @returns {Object} Header props
   */
  const handleGetHeaderProps = useCallback(
    () => ({
      style: {
        display: 'flex',
        alignItems: 'center',
      },
    }),
    []
  );

  /**
   * Creates props for table cells, including a native hover tooltip.
   * @function
   * @param {Object} cell - Table cell data
   * @returns {Object} Cell props
   */
  const handleGetCellProps = useCallback(
    (cell) => ({
      title: cell?.value == null ? undefined : String(cell.value),
    }),
    []
  );

  const columns =
    value?.columns?.length > 0
      ? createDynamicColumns({
          data: value.columns,
          width: 150,
          HeaderCell: ReactMuiTableColumnHeaderTextEllipsis,
        })
      : [];

  const filteredTableData =
    searchTerm && isArray(tableData)
      ? tableData.filter((row) =>
          Object.values(row || {}).some((cellValue) =>
            String(cellValue ?? '')
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          )
        )
      : tableData;

  return (
    <FormControl fullWidth>
      {!tableData?.length && (
        <Box
          {...getRootProps({
            className: 'dropzone',
            role: 'button',
            'aria-label': 'Click to upload or drag and drop a CSV file',
            tabIndex: 0,
          })}
          sx={{
            cursor: 'pointer',
            height: 150,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <input aria-label="Upload CSV file" {...getInputProps()} />
          <Stack sx={{ alignItems: 'center' }} spacing={0.5}>
            <CloudUploadIcon width={24} height={24} style={{ opacity: 0.7 }} />
            <Typography variant="body2">
              {isDragActive
                ? 'Drop the CSV file here.'
                : 'Drag & drop CSV here or click to upload'}
            </Typography>
            <Typography variant="body3" color="text.secondary">
              Supports CSV files
            </Typography>
          </Stack>
        </Box>
      )}

      {tableData?.length > 0 && (
        <Stack spacing={2} sx={{ mt: -1, mb: -2, width: '100%' }}>
          <input aria-label="Upload CSV file" {...getInputProps()} />
          <Stack
            component={Paper}
            variant="outlined"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              padding: 2,
              bgcolor: 'action.hover',
              borderRadius: '6px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              minWidth={0}
              sx={{ flex: 1 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'selection.main',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  '& svg': {
                    display: 'block',
                  },
                }}
              >
                <FileTextIcon width={20} height={20} />
              </Box>
              <Stack minWidth={0} sx={{ flex: 1 }}>
                <Typography variant="h6" component="span" noWrap>
                  {value?.file_name || 'CSV file'}
                </Typography>
                <Typography variant="body3" color="text.secondary" noWrap>
                  {`${tableData.length} rows loaded successfully`}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              spacing={1.5}
              flexShrink={0}
              alignItems="center"
            >
              <Button
                variant="outlined"
                onClick={open}
                startIcon={<CloudUploadIcon width={18} height={18} />}
              >
                Change CSV
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={handleRemoveFile}
                startIcon={<TrashIcon width={18} height={18} />}
              >
                Remove File
              </Button>
            </Stack>
          </Stack>
          <SearchTextField
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search table rows"
            aria-label="Search table rows"
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
            }}
          />
          <ReactMuiTableVirtualizedV8
            data={isArray(filteredTableData) ? filteredTableData : []}
            columns={columns}
            enableRowSelection={false}
            enablePagination={true}
            initialPageSize={5}
            getHeaderProps={handleGetHeaderProps}
            getColumnProps={handleGetTableColumnProps}
            getCellProps={handleGetCellProps}
            rowsPerPageOptions={[5, 10, 25]}
            showToolbar={false}
            stickyHeader
            maxTableHeight={300}
          />
        </Stack>
      )}
    </FormControl>
  );
};

TableFieldComponent.propTypes = {
  value: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string),
    data: PropTypes.arrayOf(PropTypes.shape({})),
    file_name: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
};

export default TableFieldComponent;
